// src/components/UnreviewedPackets.jsx
import React, { useMemo, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Chip,
  Divider,
  Alert,
  TextField,
  MenuItem,
  Tooltip,
  IconButton,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import { RefreshCw, CheckCircle2, Copy, ExternalLink, MapPin, Gauge, Key, Battery, Radio, ShieldCheck, HelpCircle } from "lucide-react";
import ProtocolExportButton from "./ProtocolExportButton";

import {
  useGetUnreviewedPacketsQuery,
  useLazyAnalyzePacketQuery,
  useReviewPacketMutation,
  useGetProtocolsQuery,
} from "../../redux/protocol/protocolApi";

export default function UnreviewedPackets() {
  const theme = useTheme();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [selectedId, setSelectedId] = useState(null);
  const [copied, setCopied] = useState(false);

  const [reviewForm, setReviewForm] = useState({ notes: "", protocolId: "" });

  const { data: packetsData, isLoading, error, refetch } = useGetUnreviewedPacketsQuery({
    page: page + 1,
    limit: pageSize,
  });

  const { data: protocolsData } = useGetProtocolsQuery({ status: "active" });

  const [analyzePacket, { data: analysisData, isFetching: isAnalyzing }] =
    useLazyAnalyzePacketQuery();

  const [reviewPacket, { isLoading: isReviewing }] = useReviewPacketMutation();

  const packets = packetsData?.data || [];
  const pagination = packetsData?.pagination;
  const protocols = protocolsData?.data || [];
  const analysis = analysisData?.data;
  const friendly = analysis?.friendlyData;

  const rows = useMemo(() => {
    return (packets || []).map((p) => ({
      id: p._id,
      remoteAddress: p.remoteAddress || "-",
      remotePort: p.remotePort || 0,
      imei: p.imei || p.deviceInfo?.imei || "Unknown",
      vehicleName: p.vehicleName || null,
      isRegistered: !!p.isRegistered,
      packetLength: p.packetLength ?? (p.rawHex ? p.rawHex.length / 2 : 0),
      repeatCount: p.repeatCount ?? 1,
      createdAt: p.createdAt,
      header: p.detectedFingerprint?.header || (p.rawHex ? p.rawHex.slice(0, 4) : "N/A"),
      patternType: p.detectedFingerprint?.patternType || p.matchStatus || "UNKNOWN",
      rawHex: p.rawHex,
      _raw: p,
    }));
  }, [packets]);

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const columns = useMemo(
    () => [
      {
        field: "imei",
        headerName: "Device / IMEI",
        minWidth: 220,
        flex: 1.2,
        renderCell: (params) => {
          const row = params.row;
          if (row.isRegistered) {
            return (
              <Stack direction="row" spacing={0.8} alignItems="center">
                <Chip
                  size="small"
                  label={row.vehicleName || "Registered"}
                  color="primary"
                  sx={{ fontWeight: 800, fontSize: "0.74rem", height: 22 }}
                />
                <Typography variant="caption" sx={{ color: "text.secondary", fontFamily: "monospace", fontWeight: 700 }}>
                  {row.imei}
                </Typography>
              </Stack>
            );
          }
          return (
            <Stack direction="row" spacing={0.8} alignItems="center">
              <Chip
                size="small"
                label="🟢 NEW TRACKER"
                sx={{
                  bgcolor: alpha(theme.palette.success.main, 0.15),
                  color: theme.palette.success.dark,
                  fontWeight: 900,
                  fontSize: "0.7rem",
                  height: 22,
                }}
              />
              <Typography variant="body2" sx={{ fontWeight: 800, fontFamily: "monospace" }}>
                {row.imei !== "Unknown" ? row.imei : "Detecting IMEI…"}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: "remoteAddress",
        headerName: "Device IP",
        minWidth: 140,
        renderCell: (params) => (
          <Typography variant="caption" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
            {params.row.remoteAddress}
          </Typography>
        ),
      },
      {
        field: "packetLength",
        headerName: "Size",
        width: 80,
        renderCell: (params) => (
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {params.row.packetLength} B
          </Typography>
        ),
      },
      {
        field: "header",
        headerName: "Header",
        width: 90,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.row.header}
            sx={{
              fontWeight: 900,
              borderRadius: 1.2,
              fontFamily: "monospace",
              height: 22,
              bgcolor: alpha(theme.palette.info.main, 0.1),
            }}
          />
        ),
      },
      {
        field: "patternType",
        headerName: "Data Type",
        width: 130,
        renderCell: (params) => {
          const type = String(params.row.patternType || "UNKNOWN").toUpperCase();
          let color = "default";
          if (type.includes("LOCATION")) color = "success";
          else if (type.includes("LOGIN")) color = "primary";
          else if (type.includes("ALARM")) color = "error";
          else if (type.includes("STATUS")) color = "warning";
          return (
            <Chip
              size="small"
              label={type}
              color={color}
              variant="outlined"
              sx={{ fontWeight: 800, fontSize: "0.72rem", height: 22 }}
            />
          );
        },
      },
      {
        field: "createdAt",
        headerName: "Received Time",
        minWidth: 120,
        renderCell: (params) => {
          const date = params.row.createdAt ? new Date(params.row.createdAt) : null;
          return (
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {date ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "-"}
            </Typography>
          );
        },
      },
    ],
    [theme]
  );

  const selectedRow = useMemo(() => {
    return rows.find((r) => r.id === selectedId) || null;
  }, [rows, selectedId]);

  const selectRow = useCallback(
    async (id) => {
      setSelectedId(id || null);
      const row = rows.find((r) => r.id === id);
      if (row?._raw?._id) {
        analyzePacket(row._raw._id);
      }
    },
    [rows, analyzePacket]
  );

  const handleReview = async () => {
    if (!selectedRow?._raw?._id) return;

    try {
      await reviewPacket({
        packetId: selectedRow._raw._id,
        notes: reviewForm.notes,
        protocolId: reviewForm.protocolId || undefined,
      }).unwrap();

      setSelectedId(null);
      setReviewForm({ notes: "", protocolId: "" });
      refetch();
    } catch (err) {
      alert("Error: " + (err?.data?.error || err?.message || "Review failed"));
    }
  };

  const rowCount = pagination?.total ?? rows.length;
  const totalPages = pagination?.pages ?? Math.ceil(rowCount / pageSize);

  const exportConfig = useMemo(() => {
    return {
      title: "Live GPS Packets",
      filename: `packets_${new Date().toISOString().slice(0, 10)}`,
      columns: [
        { label: "IMEI", key: "imei" },
        { label: "Vehicle", key: "vehicleName" },
        { label: "IP", key: "remoteAddress" },
        { label: "Size (B)", key: "packetLength" },
        { label: "Header", key: "header" },
        { label: "Type", key: "patternType" },
        { label: "Time", key: "createdAt" },
        { label: "Hex", key: "rawHex" },
      ],
      data: rows,
    };
  }, [rows]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Top Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>
            Live Packet Monitor & Analyzer
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Live GPS device signals on ports 5024 (G17S) & 5030 (GT06). Click any row to view decoded vehicle details.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <ProtocolExportButton config={exportConfig} />
          <Button
            onClick={() => refetch()}
            variant="contained"
            color="primary"
            startIcon={<RefreshCw size={16} />}
            sx={{ borderRadius: 2, fontWeight: 900, textTransform: "none", px: 2 }}
          >
            Refresh Packets
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error?.data?.error || "Failed to load packets"}
        </Alert>
      )}

      {/* Split layout */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1.25fr 1fr" },
          gap: 2,
          alignItems: "start",
        }}
      >
        {/* LEFT: Live Packets Table */}
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.03) }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Radio size={18} color={theme.palette.success.main} />
              <Typography sx={{ fontWeight: 900 }}>
                Live Packets ({rowCount})
              </Typography>
            </Stack>

            <Chip
              size="small"
              label={`Page ${page + 1} of ${totalPages || 1}`}
              variant="outlined"
              sx={{ fontWeight: 800 }}
            />
          </Stack>
          <Divider />

          <Box sx={{ height: 580 }}>
            <DataGrid
              rows={rows || []}
              columns={columns}
              loading={isLoading}
              disableSelectionOnClick
              selectionModel={selectedId ? [selectedId] : []}
              onSelectionModelChange={(m) => selectRow(m?.[0] || null)}
              pagination
              paginationMode="server"
              rowCount={rowCount}
              page={page}
              onPageChange={(newPage) => setPage(newPage)}
              pageSize={pageSize}
              onPageSizeChange={(newSize) => {
                setPageSize(newSize);
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{
                border: "none",
                "& .MuiDataGrid-columnHeaders": {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  fontWeight: 900,
                },
                "& .MuiDataGrid-row:hover": {
                  cursor: "pointer",
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                },
                "& .MuiDataGrid-row.Mui-selected": {
                  bgcolor: alpha(theme.palette.primary.main, 0.12) + " !important",
                },
              }}
            />
          </Box>
        </Paper>

        {/* RIGHT: Human-Readable Analysis */}
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.03) }}
          >
            <Typography sx={{ fontWeight: 900 }}>
              Device Analysis & Decoding
            </Typography>
            {selectedRow ? (
              <Chip
                size="small"
                label={friendly?.statusBadge || selectedRow.imei}
                color={friendly?.isRegistered ? "primary" : "success"}
                sx={{ fontWeight: 800 }}
              />
            ) : (
              <Chip size="small" label="No Packet Selected" variant="outlined" />
            )}
          </Stack>
          <Divider />

          <Box sx={{ p: 2 }}>
            {!selectedRow ? (
              <Box sx={{ py: 6, textAlign: "center" }}>
                <HelpCircle size={44} color={theme.palette.text.secondary} style={{ opacity: 0.5, marginBottom: 8 }} />
                <Typography sx={{ fontWeight: 800, color: "text.primary", mb: 0.5 }}>
                  Select a Packet from the Table
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320, mx: "auto" }}>
                  Click on any live row on the left to see the decoded GPS coordinates, IMEI, vehicle status, and protocol info.
                </Typography>
              </Box>
            ) : isAnalyzing ? (
              <Alert severity="info">Analyzing and decoding packet signals…</Alert>
            ) : !analysis ? (
              <Alert severity="warning">Failed to decode packet.</Alert>
            ) : (
              <Stack spacing={2}>
                {/* 1. Device Identification Banner */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2.5,
                    bgcolor: friendly?.isRegistered
                      ? alpha(theme.palette.primary.main, 0.05)
                      : alpha(theme.palette.success.main, 0.08),
                    borderColor: friendly?.isRegistered
                      ? alpha(theme.palette.primary.main, 0.25)
                      : alpha(theme.palette.success.main, 0.35),
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 900,
                        color: friendly?.isRegistered ? "primary.main" : "success.dark",
                      }}
                    >
                      {friendly?.isRegistered
                        ? `🚗 Registered Vehicle: ${friendly.vehicleName}`
                        : "🟢 NEW UNREGISTERED GPS DEVICE"}
                    </Typography>
                    <Chip
                      size="small"
                      label={friendly?.isRegistered ? "Active Fleet" : "New Signal"}
                      color={friendly?.isRegistered ? "primary" : "success"}
                      sx={{ fontWeight: 800 }}
                    />
                  </Stack>

                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 900, fontFamily: "monospace" }}>
                      {friendly?.imei || selectedRow.imei}
                    </Typography>
                    <Tooltip title={copied ? "Copied!" : "Copy IMEI"}>
                      <IconButton size="small" onClick={() => copyToClipboard(friendly?.imei || selectedRow.imei)}>
                        <Copy size={16} />
                      </IconButton>
                    </Tooltip>
                  </Stack>

                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                    Protocol: <strong>{friendly?.protocol || "GT06"}</strong> · Port:{" "}
                    <strong>{friendly?.port || 5030}</strong> · IP: {friendly?.ip || selectedRow.remoteAddress}
                  </Typography>
                </Paper>

                {/* 2. Live Telemetry / GPS Metrics */}
                {friendly?.latitude && friendly?.longitude ? (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                      <Typography sx={{ fontWeight: 900, fontSize: 13, color: "text.secondary" }}>
                        📍 LIVE GPS POSITION
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<ExternalLink size={14} />}
                        href={`https://maps.google.com/?q=${friendly.latitude},${friendly.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textTransform: "none", fontWeight: 800, borderRadius: 1.5, fontSize: 12, py: 0.3 }}
                      >
                        Open Google Maps
                      </Button>
                    </Stack>

                    <Typography sx={{ fontFamily: "monospace", fontWeight: 800, fontSize: 15, mb: 1.5 }}>
                      {friendly.latitude.toFixed(6)}, {friendly.longitude.toFixed(6)}
                    </Typography>

                    <Divider sx={{ my: 1 }} />

                    <Stack direction="row" spacing={2} justifyContent="space-around" sx={{ textAlign: "center", pt: 0.5 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                          <Gauge size={13} /> Speed
                        </Typography>
                        <Typography sx={{ fontWeight: 900, fontSize: 15 }}>
                          {friendly.speed || "0 km/h"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                          <Key size={13} /> Ignition (ACC)
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 900,
                            fontSize: 15,
                            color: friendly.ignition === "ON" ? "success.main" : "error.main",
                          }}
                        >
                          {friendly.ignition || "OFF"}
                        </Typography>
                      </Box>
                      {friendly.battery && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                            <Battery size={13} /> Battery
                          </Typography>
                          <Typography sx={{ fontWeight: 900, fontSize: 15, color: "info.main" }}>
                            {friendly.battery}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Paper>
                ) : (
                  <Paper variant="outlined" sx={{ p: 1.8, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.04) }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 13, color: "info.dark" }}>
                      📡 Handshake Packet: {friendly?.packetType || selectedRow.patternType}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.5, display: "block" }}>
                      This is a connection/handshake signal from the tracker verifying socket connectivity. GPS coordinates update as soon as vehicle moves.
                    </Typography>
                  </Paper>
                )}

                {/* 3. Raw Hex for Technical Verification */}
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 900, color: "text.secondary" }}>
                    RAW HEX PAYLOAD ({selectedRow.packetLength} BYTES)
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      mt: 0.5,
                      p: 1.2,
                      borderRadius: 2,
                      fontSize: 11,
                      overflow: "auto",
                      bgcolor: alpha(theme.palette.text.primary, 0.04),
                      border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                      fontFamily: "ui-monospace, monospace",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-all",
                    }}
                  >
                    {selectedRow.rawHex}
                  </Box>
                </Box>
              </Stack>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
